/**
 * The food catalogue: two binary trees joined by an intersection table.
 *
 * The first pass at this - and the 2016 app before it - asked "Italian? Mexican? Thai?"
 * in sequence, which costs O(n) questions for n cuisines and gives up with *no* cuisine
 * at all once the user has said no a few times. The second pass fixed the cost with a
 * single binary descent, but paid for it in two other ways:
 *
 *   1. The descent was a geography quiz. "Eastern or Western food?" -> "Europe or the
 *      Americas?" -> "Southern or Northern Europe?" are three questions nobody is hungry
 *      for, carrying no type and meaning nothing to the person answering. They existed
 *      only as binary-search scaffolding, which is the data structure leaking into the UX.
 *   2. A dish was reachable only as a leaf under its ethnic parent, so "I want pizza"
 *      cost five answers and "I want a burger" required first declaring an interest in
 *      the Americas. There was no way in from the dish side at all.
 *
 * What the old leaves actually encoded was a *product*: (italian, pizza), (japanese,
 * sushi), (korean, bbq), (greek, gyros), (british, fish and chips). `PAIRS` below makes
 * that product explicit, which is what lets a run enter from either side and still land
 * on the same precise Table A type.
 *
 * So: `CUISINE_TREE` descends traditions, `DISH_TREE` descends food types, a routing
 * question in questions.ts picks which one a run leads with, and `DISH_FOLLOWUPS` asks
 * the other axis afterwards where the answer would sharpen the type.
 *
 * Both trees stay strictly binary - every branch is one yes/no - and both are weighted by
 * prevalence rather than balanced: `probe` puts a common answer at depth+1 and pushes the
 * rest down, so Italian, Chinese, Japanese, Indian, Mexican and Thai land three or four
 * questions in while the long tail goes deeper. That is Huffman coding, not the 2016
 * ladder: a "no" here stays inside a subtree that has already set a type, so rejecting
 * Japanese still leaves `asian_restaurant` behind. The old ladder learned nothing from a
 * rejection, which was the real complaint against it.
 *
 * Every `type` is a real Google Places (New) Table A value, checked against the published
 * table - an unknown value is a 400 from the API, not a soft failure. Both trees and
 * `PAIRS` are pinned by `tests/mapping.test.ts`.
 *
 * Deliberately *not* used: `western_restaurant`. It exists in Table A, but it means
 * Western-*style* food and is applied almost entirely outside Europe and the Americas, so
 * as a fallback for European food it would return nothing in London or Chicago.
 */

export type CuisineLevel = 'region' | 'cuisine' | 'dish';

export interface CuisineNode {
  id: string;
  level: CuisineLevel;
  /**
   * The broadest Table A type that is true of this whole subtree. Set on entry, so a
   * run that stops early - question cap, or "Don't mind" - still has a usable filter.
   * Absent when the node adds nothing to what its parent already said: the inherited
   * type stands.
   */
  type?: string;
  /** Absent on a leaf. Both sides lead somewhere; `labels` name them. */
  branch?: CuisineBranch;
}

export interface CuisineBranch {
  prompt: string;
  labels: { yes: string; no: string };
  yes: CuisineNode;
  no: CuisineNode;
  /**
   * Advanced mode only. Marked per-branch rather than inferred from the children's
   * `level`, because the dish tree is made entirely of dish-level nodes and simple mode
   * has to be able to walk it - the old inference would have hidden the whole thing.
   */
  advanced?: boolean;
}

/** A leaf: the type it carries is as narrow as this path gets. */
const leaf = (id: string, level: CuisineLevel, type: string): CuisineNode => ({
  id,
  level,
  type,
});

/**
 * The "no" side of a yes/no refinement - "Pizza specifically?" / "Keep it broad".
 * Carries no type of its own, so the parent's stands.
 */
const broad = (id: string): CuisineNode => ({ id, level: 'dish' });

/** A grouping node: exists to halve the catalogue, adds no type of its own. */
const group = (id: string, branch: CuisineBranch, type?: string): CuisineNode => ({
  id,
  level: 'region',
  ...(type ? { type } : {}),
  branch,
});

const split = (
  prompt: string,
  yesLabel: string,
  noLabel: string,
  yes: CuisineNode,
  no: CuisineNode,
): CuisineBranch => ({ prompt, labels: { yes: yesLabel, no: noLabel }, yes, no });

/** A yes/no probe, for a branch where one candidate dominates its siblings. */
const probe = (prompt: string, yes: CuisineNode, no: CuisineNode): CuisineBranch =>
  split(prompt, 'Yes', 'Something else', yes, no);

/** Advanced-only refinement inside a tradition, once the tradition is settled. */
const refine = (branch: CuisineBranch): CuisineBranch => ({ ...branch, advanced: true });

// ====================================================================================
// The tradition tree
// ====================================================================================
//
// Internal nodes name their members where they can ("Japanese, Chinese or Korean?")
// rather than a region ("East Asian or South & Southeast Asian?"), because the first is
// a question about food and the second is a question about a map.

// -- Asia ----------------------------------------------------------------------------

const JAPANESE: CuisineNode = {
  ...leaf('japanese', 'cuisine', 'japanese_restaurant'),
  branch: refine(
    split(
      'Sushi or ramen?',
      'Sushi',
      'Ramen',
      leaf('sushi', 'dish', 'sushi_restaurant'),
      leaf('ramen', 'dish', 'ramen_restaurant'),
    ),
  ),
};

const CHINESE: CuisineNode = {
  ...leaf('chinese', 'cuisine', 'chinese_restaurant'),
  branch: refine(
    split(
      'Dim sum specifically?',
      'Dim sum',
      'Keep it broad',
      leaf('dim-sum', 'dish', 'dim_sum_restaurant'),
      broad('chinese-broad'),
    ),
  ),
};

const KOREAN: CuisineNode = {
  ...leaf('korean', 'cuisine', 'korean_restaurant'),
  branch: refine(
    split(
      'Korean barbecue specifically?',
      'Korean barbecue',
      'Keep it broad',
      leaf('korean-bbq', 'dish', 'korean_barbecue_restaurant'),
      broad('korean-broad'),
    ),
  ),
};

const INDIAN: CuisineNode = {
  // No type: `subcontinent` above already says `indian_restaurant`, and a node that
  // restates its parent is a rung on the widening ladder that goes nowhere.
  id: 'indian',
  level: 'cuisine',
  branch: refine(
    split(
      'South Indian or North Indian?',
      'South Indian',
      'North Indian',
      leaf('south-indian', 'dish', 'south_indian_restaurant'),
      leaf('north-indian', 'dish', 'north_indian_restaurant'),
    ),
  ),
};

const EAST_ASIA: CuisineNode = group(
  'east-asia',
  probe(
    'Japanese?',
    JAPANESE,
    group(
      'east-asia-other',
      probe(
        'Chinese?',
        CHINESE,
        group(
          'korea-taiwan',
          split(
            'Korean or Taiwanese?',
            'Korean',
            'Taiwanese',
            KOREAN,
            leaf('taiwanese', 'cuisine', 'taiwanese_restaurant'),
          ),
        ),
      ),
    ),
  ),
);

/**
 * Pakistani food is very often tagged `indian_restaurant` by Google as well, so the
 * subcontinent node carries that type: stopping here is a search that finds both.
 */
const SUBCONTINENT: CuisineNode = group(
  'subcontinent',
  split(
    'Indian or Pakistani?',
    'Indian',
    'Pakistani',
    INDIAN,
    leaf('pakistani', 'cuisine', 'pakistani_restaurant'),
  ),
  'indian_restaurant',
);

const SOUTHEAST_ASIA: CuisineNode = group(
  'southeast-asia',
  probe(
    'Thai?',
    leaf('thai', 'cuisine', 'thai_restaurant'),
    group(
      'southeast-asia-other',
      probe(
        'Vietnamese?',
        leaf('vietnamese', 'cuisine', 'vietnamese_restaurant'),
        group(
          'island-southeast-asia',
          split(
            'Filipino or Indonesian?',
            'Filipino',
            'Indonesian',
            leaf('filipino', 'cuisine', 'filipino_restaurant'),
            leaf('indonesian', 'cuisine', 'indonesian_restaurant'),
          ),
        ),
      ),
    ),
  ),
);

const ASIA: CuisineNode = group(
  'asia',
  split(
    'Japanese, Chinese or Korean?',
    'One of those',
    'Further south',
    EAST_ASIA,
    group(
      'south-asia',
      probe('Indian or Pakistani?', SUBCONTINENT, SOUTHEAST_ASIA),
    ),
  ),
  'asian_restaurant',
);

// -- The Mediterranean and the Middle East -------------------------------------------

const ITALIAN: CuisineNode = {
  ...leaf('italian', 'cuisine', 'italian_restaurant'),
  branch: refine(
    split(
      'Pizza specifically?',
      'Pizza',
      'Keep it broad',
      leaf('pizza', 'dish', 'pizza_restaurant'),
      broad('italian-broad'),
    ),
  ),
};

const GREEK: CuisineNode = {
  ...leaf('greek', 'cuisine', 'greek_restaurant'),
  branch: refine(
    split(
      'Gyros specifically?',
      'Gyros',
      'Keep it broad',
      leaf('gyros', 'dish', 'gyro_restaurant'),
      broad('greek-broad'),
    ),
  ),
};

const SPANISH: CuisineNode = {
  ...leaf('spanish', 'cuisine', 'spanish_restaurant'),
  branch: refine(
    split(
      'Tapas specifically?',
      'Tapas',
      'Keep it broad',
      leaf('tapas', 'dish', 'tapas_restaurant'),
      broad('spanish-broad'),
    ),
  ),
};

const LEBANESE: CuisineNode = {
  ...leaf('lebanese', 'cuisine', 'lebanese_restaurant'),
  branch: refine(
    split(
      'Shawarma or falafel?',
      'Shawarma',
      'Falafel',
      leaf('shawarma', 'dish', 'shawarma_restaurant'),
      leaf('falafel', 'dish', 'falafel_restaurant'),
    ),
  ),
};

const TURKISH: CuisineNode = {
  ...leaf('turkish', 'cuisine', 'turkish_restaurant'),
  branch: refine(
    split(
      'Kebabs specifically?',
      'Kebabs',
      'Keep it broad',
      leaf('kebab', 'dish', 'kebab_shop'),
      broad('turkish-broad'),
    ),
  ),
};

const MIDDLE_EAST: CuisineNode = group(
  'middle-east',
  probe(
    'Lebanese?',
    LEBANESE,
    group(
      'middle-east-other',
      split(
        'Turkish or Persian?',
        'Turkish',
        'Persian',
        TURKISH,
        leaf('persian', 'cuisine', 'persian_restaurant'),
      ),
    ),
  ),
  'middle_eastern_restaurant',
);

// No type of its own: `mediterranean` above already says `mediterranean_restaurant`.
const SOUTHERN_EUROPE: CuisineNode = group(
  'southern-europe',
  probe(
    'Greek?',
    GREEK,
    group(
      'iberia',
      split(
        'Spanish or Portuguese?',
        'Spanish',
        'Portuguese',
        SPANISH,
        leaf('portuguese', 'cuisine', 'portuguese_restaurant'),
      ),
    ),
  ),
);

const MEDITERRANEAN: CuisineNode = group(
  'mediterranean',
  probe(
    'Italian?',
    ITALIAN,
    group(
      'mediterranean-other',
      split(
        'Middle Eastern?',
        'Middle Eastern',
        'Greek, Spanish or Portuguese',
        MIDDLE_EAST,
        SOUTHERN_EUROPE,
      ),
    ),
  ),
  'mediterranean_restaurant',
);

// -- The Americas --------------------------------------------------------------------

const MEXICAN: CuisineNode = {
  ...leaf('mexican', 'cuisine', 'mexican_restaurant'),
  branch: refine(
    split(
      'Tacos specifically?',
      'Tacos',
      'Keep it broad',
      leaf('tacos', 'dish', 'taco_restaurant'),
      broad('mexican-broad'),
    ),
  ),
};

const UNITED_STATES: CuisineNode = {
  ...leaf('us', 'cuisine', 'american_restaurant'),
  branch: refine(
    probe(
      'Burgers?',
      leaf('burgers', 'dish', 'hamburger_restaurant'),
      group(
        'us-grill',
        split(
          'Steak or barbecue?',
          'Steak',
          'Barbecue',
          leaf('steak', 'dish', 'steak_house'),
          leaf('barbecue', 'dish', 'barbecue_restaurant'),
        ),
      ),
    ),
  ),
};

/**
 * `caribbean_restaurant` and `south_american_restaurant` are both real Table A types, so
 * a simple run that stops here has a genuinely specific search rather than a regional
 * shrug. The country-level split below them is advanced only.
 */
const SOUTH_AMERICA: CuisineNode = {
  ...leaf('south-america', 'cuisine', 'south_american_restaurant'),
  branch: refine(
    probe(
      'Brazilian?',
      leaf('brazilian', 'cuisine', 'brazilian_restaurant'),
      group(
        'south-america-other',
        split(
          'Peruvian or Argentinian?',
          'Peruvian',
          'Argentinian',
          leaf('peruvian', 'cuisine', 'peruvian_restaurant'),
          leaf('argentinian', 'cuisine', 'argentinian_restaurant'),
        ),
      ),
    ),
  ),
};

const CARIBBEAN: CuisineNode = {
  ...leaf('caribbean', 'cuisine', 'caribbean_restaurant'),
  branch: refine(
    split(
      'Cuban specifically?',
      'Cuban',
      'Keep it broad',
      leaf('cuban', 'cuisine', 'cuban_restaurant'),
      broad('caribbean-broad'),
    ),
  ),
};

const AMERICAS: CuisineNode = group(
  'americas',
  probe(
    'Mexican?',
    MEXICAN,
    group(
      'americas-other',
      split(
        'American - burgers, barbecue, that sort of thing?',
        'American',
        'Further south',
        UNITED_STATES,
        group(
          'latin-america',
          split(
            'Caribbean or South American?',
            'Caribbean',
            'South American',
            CARIBBEAN,
            SOUTH_AMERICA,
          ),
          'latin_american_restaurant',
        ),
      ),
    ),
  ),
);

// -- Northern Europe and Africa ------------------------------------------------------

const BRITISH: CuisineNode = {
  ...leaf('british', 'cuisine', 'british_restaurant'),
  branch: refine(
    split(
      'Fish and chips specifically?',
      'Fish and chips',
      'Keep it broad',
      leaf('fish-and-chips', 'dish', 'fish_and_chips_restaurant'),
      broad('british-broad'),
    ),
  ),
};

/**
 * Kept to three: French, British, German. Irish and Polish are both real Table A values
 * and both were drafted in here, but either one pushes this branch to depth seven, and
 * simple mode's cap is six - so they would only ever have been reachable in advanced
 * mode, while costing every simple run an extra question to walk past. Two traditions
 * nobody can reach is a worse trade than two traditions that aren't offered.
 */
const NORTHERN_EUROPE: CuisineNode = group(
  'northern-europe',
  probe(
    'French?',
    leaf('french', 'cuisine', 'french_restaurant'),
    group(
      'northern-europe-other',
      split(
        'British or German?',
        'British',
        'German',
        BRITISH,
        leaf('german', 'cuisine', 'german_restaurant'),
      ),
    ),
  ),
  'european_restaurant',
);

const AFRICA: CuisineNode = group(
  'africa',
  split(
    'North African, or further south?',
    'North African',
    'Further south',
    leaf('moroccan', 'cuisine', 'moroccan_restaurant'),
    leaf('ethiopian', 'cuisine', 'ethiopian_restaurant'),
  ),
  'african_restaurant',
);

/**
 * The tradition tree. There is no "Eastern or Western food?" at the top any more: the
 * routing question in questions.ts has already asked something more useful, and the first
 * real split here is one people answer without thinking about a map.
 */
export const CUISINE_TREE: CuisineNode = group(
  'root',
  probe(
    'Asian food?',
    ASIA,
    group(
      'not-asia',
      probe(
        'Mediterranean or Middle Eastern?',
        MEDITERRANEAN,
        group(
          'not-mediterranean',
          split(
            'Food from the Americas?',
            'The Americas',
            'Europe or Africa',
            AMERICAS,
            group(
              'europe-africa',
              probe('African?', AFRICA, NORTHERN_EUROPE),
            ),
          ),
        ),
      ),
    ),
  ),
);

// ====================================================================================
// The dish tree
// ====================================================================================
//
// Weighted the same way: pizza, burger and sushi are the cravings people arrive with, so
// they sit at the top. Grouping nodes carry no type - "something in bread" is not a
// search - and the leaves carry a standalone Table A type where one exists. Where the
// dish is only precise once a tradition is known (a wrap is a taco or a kebab or a gyro),
// `DISH_FOLLOWUPS` asks the other axis and `PAIRS` resolves it.

const WRAP: CuisineNode = group(
  'wrap',
  split(
    'Mexican, or Middle Eastern?',
    'Mexican',
    'Middle Eastern',
    leaf('taco', 'dish', 'taco_restaurant'),
    // Kebab, shawarma and gyros are three different Table A values and which one this
    // means is exactly what the tradition follow-up settles. Until it does,
    // `middle_eastern_restaurant` is the honest cover for all three - and it has to
    // carry *something*, because this is a leaf and skipping the follow-up would
    // otherwise drop the run back to searching for nothing in particular.
    leaf('flatbread', 'dish', 'middle_eastern_restaurant'),
  ),
);

const HANDHELD: CuisineNode = group(
  'handheld',
  probe(
    'Pizza?',
    leaf('pizza-dish', 'dish', 'pizza_restaurant'),
    group(
      'handheld-other',
      probe(
        'Burger?',
        leaf('burger', 'dish', 'hamburger_restaurant'),
        group(
          'handheld-rest',
          split(
            'In a wrap or flatbread?',
            'Wrap or flatbread',
            'Sandwich',
            WRAP,
            leaf('sandwich', 'dish', 'sandwich_shop'),
          ),
        ),
      ),
    ),
  ),
);

const NOODLES_AND_DUMPLINGS: CuisineNode = group(
  'noodles-dumplings',
  split(
    // Not `probe` here: its "Yes / Something else" would land straight after the
    // grouping question that already listed all three, and read as the same question
    // asked twice. Naming the other side is what makes it a different question.
    'Noodles specifically?',
    'Noodles',
    'Dumplings or hot pot',
    // `noodle_shop` is the cover type; `ramen_restaurant` and
    // `chinese_noodle_restaurant` are both real too, and the tradition follow-up is
    // what decides between them.
    leaf('noodles', 'dish', 'noodle_shop'),
    group(
      'dumplings-hotpot',
      split(
        'Dumplings or hot pot?',
        'Dumplings',
        'Hot pot',
        leaf('dumplings', 'dish', 'dumpling_restaurant'),
        leaf('hot-pot', 'dish', 'hot_pot_restaurant'),
      ),
    ),
  ),
  'asian_restaurant',
);

const GRILLED: CuisineNode = group(
  'grilled',
  probe(
    'Barbecue?',
    leaf('bbq', 'dish', 'barbecue_restaurant'),
    group(
      'grilled-other',
      split(
        'Steak or chicken?',
        'Steak',
        'Chicken',
        leaf('steak-dish', 'dish', 'steak_house'),
        leaf('chicken', 'dish', 'chicken_restaurant'),
      ),
    ),
  ),
);

const ON_A_PLATE: CuisineNode = group(
  'plate',
  probe(
    'Sushi?',
    leaf('sushi-dish', 'dish', 'sushi_restaurant'),
    group(
      'plate-other',
      probe(
        'Noodles, dumplings or hot pot?',
        NOODLES_AND_DUMPLINGS,
        group(
          'plate-rest',
          probe(
            'Grilled meat?',
            GRILLED,
            group(
              'plate-last',
              split(
                'Seafood, or something lighter?',
                'Seafood',
                'Something lighter',
                leaf('seafood-dish', 'dish', 'seafood_restaurant'),
                leaf('salad', 'dish', 'salad_shop'),
              ),
            ),
          ),
        ),
      ),
    ),
  ),
);

/** The dish tree. Entered when the routing question is answered "a dish". */
export const DISH_TREE: CuisineNode = group(
  'root',
  split(
    'Something handheld - pizza, burger, a wrap?',
    'Handheld',
    'On a plate',
    HANDHELD,
    ON_A_PLATE,
  ),
);

// ====================================================================================
// Joining the two
// ====================================================================================

/**
 * The tradition question asked *after* a dish, for the dishes where knowing the tradition
 * sharpens the type. Leaves are tradition ids, so these descend into `cuisinePath` using
 * the same machinery as `CUISINE_TREE` - a run leading with a dish ends up with both
 * axes set, which is what `PAIRS` needs.
 *
 * Only dishes with genuine multi-tradition ambiguity get one. "Pizza" does not: it is
 * `pizza_restaurant` whoever is cooking it.
 */
export const DISH_FOLLOWUPS: Record<string, CuisineNode> = {
  noodles: group(
    'noodles-tradition',
    probe(
      'Ramen?',
      leaf('japanese', 'cuisine', 'japanese_restaurant'),
      group(
        'noodles-tradition-other',
        split(
          'Chinese or Thai?',
          'Chinese',
          'Thai',
          leaf('chinese', 'cuisine', 'chinese_restaurant'),
          leaf('thai', 'cuisine', 'thai_restaurant'),
        ),
      ),
    ),
  ),
  flatbread: group(
    'flatbread-tradition',
    probe(
      'Kebab?',
      leaf('turkish', 'cuisine', 'turkish_restaurant'),
      group(
        'flatbread-tradition-other',
        split(
          'Shawarma or gyros?',
          'Shawarma',
          'Gyros',
          leaf('lebanese', 'cuisine', 'lebanese_restaurant'),
          leaf('greek', 'cuisine', 'greek_restaurant'),
        ),
      ),
    ),
  ),
  bbq: group(
    'bbq-tradition',
    split(
      'Korean barbecue, or American-style?',
      'Korean',
      'American-style',
      leaf('korean', 'cuisine', 'korean_restaurant'),
      leaf('us', 'cuisine', 'american_restaurant'),
    ),
  ),
  dumplings: group(
    'dumplings-tradition',
    split(
      'Dim sum, or keep it broad?',
      'Dim sum',
      'Keep it broad',
      leaf('chinese', 'cuisine', 'chinese_restaurant'),
      broad('dumplings-broad'),
    ),
  ),
  'seafood-dish': group(
    'seafood-tradition',
    split(
      'Fish and chips, or keep it broad?',
      'Fish and chips',
      'Keep it broad',
      leaf('british', 'cuisine', 'british_restaurant'),
      broad('seafood-broad'),
    ),
  ),
};

/**
 * Where a tradition and a dish together name a Table A type neither names alone.
 *
 * This is the table the old tree encoded implicitly, as paths: reaching
 * `sushi_restaurant` meant walking Eastern -> Asian -> East Asian -> Japanese -> Sushi,
 * and there was no other way in. Keyed `tradition:dish`, it is now reachable from both
 * directions and is the only place the two axes are allowed to interact.
 */
const PAIRS: Record<string, string> = {
  'japanese:noodles': 'ramen_restaurant',
  'chinese:noodles': 'chinese_noodle_restaurant',
  'thai:noodles': 'thai_restaurant',
  'chinese:dumplings': 'dim_sum_restaurant',
  'turkish:flatbread': 'kebab_shop',
  'lebanese:flatbread': 'shawarma_restaurant',
  'greek:flatbread': 'gyro_restaurant',
  'korean:bbq': 'korean_barbecue_restaurant',
  'us:bbq': 'barbecue_restaurant',
  'british:seafood-dish': 'fish_and_chips_restaurant',
};

/** Which axis a run led with, and how far each descent has got. */
export interface CuisineSelection {
  lead?: 'cuisine' | 'dish';
  cuisinePath: string[];
  dishPath: string[];
}

/**
 * The types picked up along a descent, broadest first.
 *
 * `['asia', 'east-asia', 'japanese']` against the tradition tree gives
 * `['asian_restaurant', 'japanese_restaurant']` - the ladder a search can climb back up
 * when the narrowest answer finds nothing. Stops early on an id that isn't in the tree
 * rather than guessing, so a stale path from an older build degrades to "no wider type
 * known".
 */
export function typesAlongPath(path: string[], root: CuisineNode): string[] {
  const types: string[] = [];
  let node: CuisineNode = root;
  if (node.type) types.push(node.type);

  for (const id of path) {
    const branch = node.branch;
    if (!branch) break;
    const next = [branch.yes, branch.no].find((child) => child.id === id);
    if (!next) break;
    node = next;
    if (node.type) types.push(node.type);
  }

  return types;
}

/**
 * A dish-led run walks `DISH_TREE` and then one of `DISH_FOLLOWUPS`, so its tradition
 * path is rooted in whichever follow-up tree the chosen dish opened.
 */
function cuisineRootFor(selection: CuisineSelection): CuisineNode {
  if (selection.lead !== 'dish') return CUISINE_TREE;
  const dish = selection.dishPath[selection.dishPath.length - 1];
  if (!dish) return CUISINE_TREE;
  return DISH_FOLLOWUPS[dish] ?? CUISINE_TREE;
}

/** The narrowest tradition and dish ids the two descents have committed to, if any. */
function endpoints(selection: CuisineSelection): { cuisine?: string; dish?: string } {
  return {
    cuisine: selection.cuisinePath[selection.cuisinePath.length - 1],
    dish: selection.dishPath[selection.dishPath.length - 1],
  };
}

/**
 * The one Table A type these answers add up to.
 *
 * Precedence is pair, then the axis the run led with, then the other one. The lead wins
 * because it is what the user opened with: someone who said "a kind of food" and then
 * "Japanese" wants Japanese, and a vaguer dish answer afterwards should not overrule it.
 */
export function resolveType(selection: CuisineSelection): string | undefined {
  const { cuisine, dish } = endpoints(selection);

  if (cuisine && dish) {
    const pair = PAIRS[`${cuisine}:${dish}`];
    if (pair) return pair;
  }

  const cuisineTypes = typesAlongPath(selection.cuisinePath, cuisineRootFor(selection));
  const dishTypes = typesAlongPath(selection.dishPath, DISH_TREE);

  const [first, second] =
    selection.lead === 'dish' ? [dishTypes, cuisineTypes] : [cuisineTypes, dishTypes];

  return first[first.length - 1] ?? second[second.length - 1];
}

/**
 * Every type this run could honestly search for, broadest first, ending on the one it
 * actually settled on. `widenFacts` in mapping.ts climbs exactly one rung of this when a
 * search comes back empty.
 *
 * A pair type sits at the end of the lead axis's ladder rather than on its own: leading
 * with "a dish" and landing on `ramen_restaurant` widens to `noodle_shop`, then
 * `asian_restaurant`, which is the honest sequence of "one step less specific".
 */
export function widenLadder(selection: CuisineSelection): string[] {
  const lead =
    selection.lead === 'dish'
      ? typesAlongPath(selection.dishPath, DISH_TREE)
      : typesAlongPath(selection.cuisinePath, cuisineRootFor(selection));

  const settled = resolveType(selection);
  const ladder = [...lead];
  if (settled && ladder[ladder.length - 1] !== settled) ladder.push(settled);

  // A tradition-led run that never chose one still has the dish ladder to fall back on.
  if (ladder.length === 0) {
    return typesAlongPath(selection.dishPath, DISH_TREE);
  }

  return ladder;
}

/** Every node in a tree, root first. Used by the tests and by the question builder. */
export function walkCuisines(
  node: CuisineNode = CUISINE_TREE,
  depth = 0,
): { node: CuisineNode; depth: number }[] {
  if (!node.branch) return [{ node, depth }];
  return [
    { node, depth },
    ...walkCuisines(node.branch.yes, depth + 1),
    ...walkCuisines(node.branch.no, depth + 1),
  ];
}

/** Every tree a run can descend: both axes plus each dish follow-up. */
function allTrees(): CuisineNode[] {
  return [CUISINE_TREE, DISH_TREE, ...Object.values(DISH_FOLLOWUPS)];
}

/** Every Table A type any descent or pair can produce. Pinned by the tests. */
export function allTypes(): string[] {
  const types = new Set<string>();
  for (const tree of allTrees()) {
    for (const { node } of walkCuisines(tree)) {
      if (node.type) types.add(node.type);
    }
  }
  for (const type of Object.values(PAIRS)) types.add(type);
  return [...types];
}

/** Exposed for the test that pins every pair key against ids that actually exist. */
export function pairKeys(): string[] {
  return Object.keys(PAIRS);
}
